import { useMemo, useState } from "react";
import { useParams } from "react-router";
import { toast } from "react-toastify";

import { HashedTicketServiceInterface } from "../interface/HashedTicketServiceInterface";

/**
 * Loads a ticket for its printable view and builds the QR targets printed on it.
 *
 * The dispatch QR points at the stream's form route rather than back at the
 * printable, mirroring the main ticket, whose printable encodes /ticket/{id}
 * (digital-ticket-view.tsx:35). Streams without haulage records pass no
 * haulRecordRoute and simply get an empty haulRecordUrl.
 */
export function useDigitalTicket<T extends { id: number }>(
  service: HashedTicketServiceInterface<T>,
  streamRoute: string,
  haulRecordRoute?: string
) {
  const { id }: any = useParams();
  const [ticket, setTicket] = useState<T | null>(null);
  const [ticketUrl, setTicketUrl] = useState("");
  const [haulRecordUrl, setHaulRecordUrl] = useState("");

  useMemo(() => {
    service.getTicket(id).then(async (response) => {
      if (!response.ok) {
        toast.error("Failed to load ticket");
        return;
      }
      const t = response.data as T;
      setTicket(t);

      const origin = window.location.origin;
      if (haulRecordRoute) {
        setHaulRecordUrl(`${origin}/${haulRecordRoute}/${t.id}`);
      }

      const hashResponse = await service.getTicketHash(t.id);
      setTicketUrl(
        hashResponse.ok
          ? `${origin}/${streamRoute}/${t.id}?hash=${hashResponse.data}`
          : `${origin}/${streamRoute}/${t.id}`
      );
    });
  }, []);

  return { ticket, ticketUrl, haulRecordUrl };
}
